/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { PrimaryButton } from "../buttons/PrimaryButton";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import axios from "axios";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useParcels } from "../../store/useParcels";
import { useDropLocations } from "../../store/useDropLocations";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
  Tooltip,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import InformationIcon from "../../assets/icons/info/information.png";
import {
  getStoreColor,
  getCityColor,
  TOTAL_CONTRAST_COLOR,
} from "../../constants/plasticColors";

dayjs.extend(isoWeek);

interface ParcelRevenueProps {
  filterType?: "location" | "city";
  filterPlaces?: string[];
  sequenceStep?: number;
  setSequenceStep?: (n: number) => void;
  sequenceStepIndex?: number;
}

export const ParcelRevenue = React.memo(
  ({
    filterType,
    filterPlaces,
    sequenceStep,
    setSequenceStep,
    sequenceStepIndex,
  }: ParcelRevenueProps) => {
    const useGeneralFilter =
      filterType !== undefined && filterPlaces !== undefined;
  /** Refs */
  const snapshotRef = useRef(null);
  /** Hooks */
    const {
      parcelRevenuePerLocation,
      getParcelRevenuePerLocation,
      isLoadingParcelRevenuePerLocation,
      isParcelRevenuePerLocationError,
    } = useParcels();
  const { dropLocations, getDropLocations } = useDropLocations();

  const [_, takeScreenshot] = useScreenshot();
  /** State */
  const [startYear, setStartYear] = useState(() => {
      const d = dayjs().subtract(12, "month");
      return d.year();
  });
  const [startMonth, setStartMonth] = useState(() => {
      const d = dayjs().subtract(12, "month");
      return d.month() + 1;
  });
  const [endYear, setEndYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(new Date().getMonth() + 1);
    const [period, setPeriod] = useState<"month" | "quarter" | "year">("month");
    const [startQuarter, setStartQuarter] = useState(1);
    const [endQuarter, setEndQuarter] = useState(4);
  /** Callbacks */
  const selectStartYear = (selectedYear: number) => {
    setStartYear(selectedYear);
  };

  const selectStartMonth = (selectedMonth: number) => {
    setStartMonth(selectedMonth);
  };

  const selectEndYear = (selectedYear: number) => {
    setEndYear(selectedYear);
  };

  const selectEndMonth = (selectedMonth: number) => {
    setEndMonth(selectedMonth);
  };

  /** Year options */
  const startYearOptions = Array.from(
    { length: dayjs().year() - 2023 },
    (_, i) => dayjs().year() - i
  );

  const endYearOptions = Array.from(
    { length: dayjs().year() - 2023 },
    (_, i) => dayjs().year() - i
  );

    const monthOptions = [
      { value: 1, label: "Jan" },
      { value: 2, label: "Feb" },
      { value: 3, label: "Mar" },
      { value: 4, label: "Apr" },
      { value: 5, label: "May" },
      { value: 6, label: "Jun" },
      { value: 7, label: "Jul" },
      { value: 8, label: "Aug" },
      { value: 9, label: "Sep" },
      { value: 10, label: "Oct" },
      { value: 11, label: "Nov" },
      { value: 12, label: "Dec" },
    ];

    const quarterOptions = [
      { value: 1, label: "Q1" },
      { value: 2, label: "Q2" },
      { value: 3, label: "Q3" },
      { value: 4, label: "Q4" },
    ];

    /** Month options */
    const startMonthOptions = useMemo(() => {
      const cy = dayjs().year();
      const cm = dayjs().month() + 1;
      return startYear === cy
        ? monthOptions.filter((m) => m.value <= cm)
        : monthOptions;
    }, [startYear]);

    const endMonthOptions = useMemo(() => {
      const cy = dayjs().year();
      const cm = dayjs().month() + 1;
      return endYear === cy
        ? monthOptions.filter((m) => m.value <= cm)
        : monthOptions;
  }, [endYear]);

  const downloadScreenshot = () => {
    takeScreenshot(snapshotRef.current).then((image: any) => {
      const link = document.createElement("a");
      link.href = image;
        link.download = "parcel_revenue.png";
        link.click();
    });
  };

    const [isCsvDownloading, setIsCsvDownloading] = useState(false);
    const downloadCsv = () => {
      setIsCsvDownloading(true);
      const params: Record<string, string> = {
        startYear: startYear.toString(),
        endYear: endYear.toString(),
        period: "daily",
      };
      params.startMonth =
        period === "year" ? "1" : period === "quarter" ? String((startQuarter - 1) * 3 + 1) : String(startMonth);
      params.endMonth =
        period === "year" ? "12" : period === "quarter" ? String(endQuarter * 3) : String(endMonth);
      if (useGeneralFilter) params.type = filterType ?? "location";

      axios
        .get("/parcels/stats/parcel-revenue-per-location", { params })
        .then((response) => {
          const series = (response.data as typeof transformedParcelRevenueData) ?? [];
          const allTs = new Set<number>();
          series.forEach((s) => {
            const d = (s.data as [number, number][] | undefined) ?? [];
            d.forEach(([ts]) => allTs.add(ts));
          });
          const sortedTs = Array.from(allTs).sort((a, b) => a - b);
          const header =
            "Date," +
            series
              .map((s) => String(s?.name ?? "").replace(/,/g, " "))
              .join(",") +
            "\n";
          const rows = sortedTs.map((ts) => {
            const values = series.map((s) => {
              const pair = ((s.data as [number, number][]) ?? []).find(([t]) => t === ts);
              const val = pair != null ? (pair[1] ?? 0) : 0;
              return (val / 100).toFixed(2);
            });
            return [dayjs(ts).format("YYYY-MM-DD"), ...values].join(",");
          });
          const csv = header + rows.join("\n");
          const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `parcel_revenue_daily_${dayjs().format("YYYY-MM-DD")}.csv`;
          link.click();
          URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error("Error downloading CSV:", error);
        })
        .finally(() => {
          setIsCsvDownloading(false);
        });
    };

    const TOTAL_ALL_STORES_ID = "__TOTAL_ALL_STORES__";

    /** Filter data based on global location/city filters */
    const filteredParcelRevenueData = useMemo(() => {
      const raw = parcelRevenuePerLocation ?? [];
      if (!useGeneralFilter) return raw;

      const currentFilterType = filterType ?? "location";
      const places = filterPlaces ?? [];
      const includeTotal = places.includes(TOTAL_ALL_STORES_ID);
      const realPlaces = places.filter((p) => p !== TOTAL_ALL_STORES_ID);

      if (places.length === 0) return raw;

      if (realPlaces.length === 0) {
        if (!includeTotal) return [];
        const base = raw;
        if (!base.length) return [];
        const firstData =
          (base[0]?.data as unknown as [number, number][] | undefined) ?? [];
        const totalData = firstData.map(([x], idx) => {
          const sum = base.reduce((acc, s) => {
            const d =
              (s?.data as unknown as [number, number][] | undefined) ?? [];
            const val = d[idx]?.[1] ?? 0;
            return acc + (Number(val) || 0);
          }, 0);
          return [x, sum] as [number, number];
        });
    return [
          { name: "Total all stores", data: totalData },
        ] as unknown as typeof raw;
      }

      let filtered: typeof raw;
      if (currentFilterType === "location") {
        const selectedCostPlaces = new Set<string>();
        realPlaces.forEach((id) => {
          const loc = dropLocations?.find((l) => String(l._id) === String(id));
          if (loc?.cost_place) {
            selectedCostPlaces.add(loc.cost_place as string);
          }
        });
        if (selectedCostPlaces.size === 0) filtered = [];
        else
          filtered = raw.filter((series) =>
            selectedCostPlaces.has(series.name as string)
          );
      } else {
        const selectedCities = new Set(realPlaces.map((c) => c.toString()));
        filtered = raw.filter((series) =>
          selectedCities.has(series.name as string)
        );
      }

      if (includeTotal && filtered.length > 0) {
        const firstData =
          (filtered[0]?.data as unknown as [number, number][] | undefined) ??
          [];
        const totalData = firstData.map(([x], idx) => {
          const sum = filtered.reduce((acc, s) => {
            const d =
              (s?.data as unknown as [number, number][] | undefined) ?? [];
            const val = d[idx]?.[1] ?? 0;
            return acc + (Number(val) || 0);
          }, 0);
          return [x, sum] as [number, number];
        });
        filtered = [
          ...filtered,
          {
            name: "Total all stores",
            data: totalData,
          } as unknown as (typeof raw)[number],
        ];
      }
      return filtered;
    }, [
      parcelRevenuePerLocation,
      useGeneralFilter,
      filterType,
      filterPlaces,
      dropLocations,
    ]);

    const transformedParcelRevenueData = useMemo(() => {
      const raw = filteredParcelRevenueData ?? [];
      if (!raw.length) return raw;

      // For month we keep original monthly time series
      if (period === "month") {
        return raw;
      }

      // Aggregate to yearly totals per series - only years in selected range
      if (period === "year") {
        return raw.map((series) => {
          const sData = (series.data as unknown as [number, number][]) ?? [];
          const yearSums = new Map<number, number>();

          sData.forEach(([ts, val]) => {
            const y = dayjs(ts).year();
            if (y >= startYear && y <= endYear) {
              yearSums.set(y, (yearSums.get(y) ?? 0) + val);
            }
          });

          const data: [number, number][] = [];
          for (let y = startYear; y <= endYear; y++) {
            data.push([new Date(y, 0, 1).getTime(), yearSums.get(y) ?? 0]);
          }

          return { ...series, data };
        });
      }

      // Aggregate to quarterly totals per series
      if (period === "quarter") {
        return raw.map((series) => {
          const sData = (series.data as unknown as [number, number][]) ?? [];
          const quarterMap = new Map<
            string,
            { year: number; q: number; sum: number }
          >();

          sData.forEach(([ts, val]) => {
            const d = dayjs(ts);
            const year = d.year();
            const q = Math.ceil((d.month() + 1) / 3);
            const key = `${year}-${q}`;
            const prev = quarterMap.get(key);
            if (prev) {
              prev.sum += val;
            } else {
              quarterMap.set(key, { year, q, sum: val });
            }
          });

          const sorted = Array.from(quarterMap.values()).sort((a, b) =>
            a.year === b.year ? a.q - b.q : a.year - b.year
          );

          const data = sorted.map(({ year, q, sum }) => {
            const monthIndex = (q - 1) * 3 + 1; // 1-based month of quarter start
            const ts = new Date(year, monthIndex - 1, 15).getTime();
            return [ts, sum] as [number, number];
          });

          return { ...series, data };
        });
      }

      return raw;
    }, [filteredParcelRevenueData, period, startYear, endYear]);

    const monthlyTickAmount = useMemo(() => {
      const firstSeries =
        (transformedParcelRevenueData?.[0]?.data as
          | [number, number][]
          | undefined) ?? [];
      return firstSeries.length ? firstSeries.length - 1 : 11;
    }, [transformedParcelRevenueData]);

    const parcelRevenueSeriesNames = useMemo(
      () =>
        (transformedParcelRevenueData ?? []).map(
          (series) => series?.name || ""
        ),
      [transformedParcelRevenueData]
    );
    const parcelRevenueColors = useMemo(() => {
      const isCityView =
        useGeneralFilter && (filterType ?? "location") === "city";
      return parcelRevenueSeriesNames.map((name) =>
        name === "Total all stores"
          ? TOTAL_CONTRAST_COLOR
          : isCityView
            ? getCityColor(name)
            : getStoreColor(name)
      );
    }, [parcelRevenueSeriesNames, useGeneralFilter, filterType]);

    const periodLabel =
      period === "month"
        ? "€/month"
        : period === "quarter"
          ? "€/quarter"
          : "€/year";

  /** Effects */
  useEffect(() => {
    getDropLocations();
  }, []);

    const prevFilterSigRef = useRef<string | null>(null);

  useEffect(() => {
      if (
        sequenceStep !== undefined &&
        setSequenceStep !== undefined &&
        sequenceStepIndex !== undefined &&
        sequenceStep < sequenceStepIndex
      ) {
        return;
      }
      const filterSig = JSON.stringify({
        period,
        startYear,
        startMonth,
        startQuarter,
        endYear,
        endMonth,
        endQuarter,
        useGeneralFilter,
        filterType,
      });
      if (
        sequenceStep !== undefined &&
        sequenceStepIndex !== undefined &&
        sequenceStep > sequenceStepIndex &&
        prevFilterSigRef.current === filterSig
      ) {
        return;
      }
      prevFilterSigRef.current = filterSig;

      let apiStartYear: number;
      let apiStartMonth: number;
      let apiEndYear: number;
      let apiEndMonth: number;

      if (period === "quarter") {
        apiStartYear = startYear;
        apiStartMonth = (startQuarter - 1) * 3 + 1;
        apiEndYear = endYear;
        apiEndMonth = endQuarter * 3;
      } else if (period === "year") {
        apiStartYear = startYear;
        apiStartMonth = 1;
        apiEndYear = endYear;
        apiEndMonth = 12;
      } else {
        apiStartYear = startYear;
        apiStartMonth = startMonth;
        apiEndYear = endYear;
        apiEndMonth = endMonth;
      }

      const promise = getParcelRevenuePerLocation(
        apiStartYear,
        apiStartMonth,
        apiEndYear,
        apiEndMonth,
        useGeneralFilter ? (filterType ?? "location") : undefined,
        undefined
      );
      if (
        setSequenceStep != null &&
        sequenceStepIndex != null &&
        sequenceStep === sequenceStepIndex
      ) {
        Promise.resolve(promise).finally(() =>
          setSequenceStep(sequenceStepIndex + 1)
        );
      }
    }, [
      period,
      startYear,
      startMonth,
      startQuarter,
      endYear,
      endMonth,
      endQuarter,
      useGeneralFilter,
      filterType,
      getParcelRevenuePerLocation,
      sequenceStep,
      setSequenceStep,
      sequenceStepIndex,
    ]);

  return (
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl shadow-none mb-1 sm:mb-10">
        <div className="flex flex-col w-full items-start">
          <div className="flex flex-col sm:flex-row w-full items-start justify-between gap-4 ">
            <div></div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap w-full min-w-0 items-start sm:items-center justify-start sm:justify-end gap-2 sm:gap-3">
              {/* Period */}
              <div className="flex items-center gap-1 sm:gap-2">
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                      {period === "month"
                        ? "Monthly"
                        : period === "quarter"
                          ? "Quarterly"
                          : "Yearly"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    <MenuItem
                      disabled={period === "month"}
                      onClick={() => setPeriod("month")}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      Monthly
                    </MenuItem>
                    <MenuItem
                      disabled={period === "quarter"}
                      onClick={() => setPeriod("quarter")}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      Quarterly
                    </MenuItem>
                    <MenuItem
                      disabled={period === "year"}
                      onClick={() => setPeriod("year")}
                      className="font-satoshi text-base text-dark"
            placeholder={undefined}
          >
                      Yearly
                    </MenuItem>
                  </MenuList>
                </Menu>
        </div>
          {/* Start Date */}
          <div className="flex items-center gap-2">
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {startYear}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
                  <MenuList placeholder={undefined}>
                {startYearOptions.map((yearOption) => (
                  <MenuItem
                    key={yearOption}
                    disabled={startYear === yearOption}
                    onClick={() => selectStartYear(yearOption)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {yearOption}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
                {period === "month" && (
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {startMonthOptions.find((m) => m.value === startMonth)
                    ?.label || "Month"}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
                    <MenuList placeholder={undefined}>
                {startMonthOptions.map((monthOption) => (
                  <MenuItem
                    key={monthOption.value}
                    disabled={startMonth === monthOption.value}
                    onClick={() => selectStartMonth(monthOption.value)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {monthOption.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
                )}
                {period === "quarter" && (
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                        {
                          quarterOptions.find((q) => q.value === startQuarter)
                            ?.label
                        }
                        <ChevronDown className="ml-1 sm:ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {quarterOptions.map((q) => (
                        <MenuItem
                          key={q.value}
                          disabled={startQuarter === q.value}
                          onClick={() => setStartQuarter(q.value)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {q.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                )}
          </div>

          {/* End Date */}
          <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  To:
                </label>
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {endYear}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
                  <MenuList placeholder={undefined}>
                {endYearOptions.map((yearOption) => (
                  <MenuItem
                    key={yearOption}
                    disabled={endYear === yearOption}
                    onClick={() => selectEndYear(yearOption)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {yearOption}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
                {period === "month" && (
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                        {endMonthOptions.find((m) => m.value === endMonth)
                          ?.label || "Month"}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
                    <MenuList placeholder={undefined}>
                {endMonthOptions.map((monthOption) => (
                  <MenuItem
                    key={monthOption.value}
                    disabled={endMonth === monthOption.value}
                    onClick={() => selectEndMonth(monthOption.value)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {monthOption.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
                )}
                {period === "quarter" && (
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                        {
                          quarterOptions.find((q) => q.value === endQuarter)
                            ?.label
                        }
                        <ChevronDown className="ml-1 sm:ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {quarterOptions.map((q) => (
                        <MenuItem
                          key={q.value}
                          disabled={endQuarter === q.value}
                          onClick={() => setEndQuarter(q.value)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {q.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                )}
          </div>

          <PrimaryButton
            size="sm"
            onClick={downloadScreenshot}
            label="Download image"
                className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
              />
              <PrimaryButton
                size="sm"
                onClick={downloadCsv}
                disabled={
                  !transformedParcelRevenueData?.length || isCsvDownloading
                }
                label={isCsvDownloading ? "..." : "Download CSV"}
                className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
            />
          </div>
          </div>
        </div>
        <section ref={snapshotRef} className="mt-4">
          <div className="flex items-center shrink-0">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
              Parcel Revenue over Time (€)
            </p>
            <Tooltip
              content="Non-cumulative: Shows parcel revenue for the selected date range"
              placeholder={undefined}
            >
              <img
                src={InformationIcon}
                alt="info"
                className="inline-block w-4 h-4 ml-2 cursor-pointer"
              />
            </Tooltip>
      </div>
          {isLoadingParcelRevenuePerLocation ? (
          <div className="flex items-center justify-center h-[300px] w-full">
            <svg
              aria-hidden="true"
                className="w-8 h-8 text-gray-200 animate-spin fill-primary-base"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </div>
          ) : isParcelRevenuePerLocationError ? (
            <div className="flex items-center justify-center h-[300px] w-full">
              <p className="text-red-500 font-satoshi">
                Failed to load data. Please try again.
              </p>
            </div>
          ) : transformedParcelRevenueData &&
            transformedParcelRevenueData.length > 0 ? (
            <Chart
              options={{
                ...options,
                legend: { show: true, position: "right" as const },
                colors:
                  parcelRevenueColors.length > 0
                    ? parcelRevenueColors
                    : options.colors,
                xaxis: {
                  // Only show 1 tick when there's only one bucket
                  tickAmount: monthlyTickAmount,
                  labels: {
                    formatter: function (value: string) {
                      const ts = Number(value);
                      if (period === "year") {
                        return dayjs(ts).format("YYYY");
                      }
                      if (period === "quarter") {
                        const d = dayjs(ts);
                        const q = Math.ceil((d.month() + 1) / 3);
                        return `Q${q} ${d.year()}`;
                      }
                      return dayjs(ts).format("MMM YYYY");
                    },
                  },
                },
              }}
              series={transformedParcelRevenueData}
              // type="line"
              height={300}
            />
          ) : null}
        </section>
      </div>
    );
  }
  );

const options: ApexOptions = {
  chart: {
    type: "line",
    zoom: {
      enabled: false,
    },
    toolbar: {
      show: false,
    },
  },
  dataLabels: {
    enabled: false,
  },
  grid: {
    strokeDashArray: 5,
  },
  markers: {
    size: 1,
  },
  legend: {
    show: true,
    position: "right",
  },
  tooltip: {
    shared: true,
    intersect: false,
    custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
      // Create an array of series with their values
      const seriesData = series.map((seriesValues: any, index: number) => ({
        name: w.globals.seriesNames[index],
        value: seriesValues[dataPointIndex],
        color: w.config.colors[index],
      }));

      // Sort by value in descending order (highest to lowest)
      seriesData.sort((a: any, b: any) => b.value - a.value);

      // Build the tooltip HTML with original ApexCharts styling
      let tooltipHtml =
        '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">';
      tooltipHtml += `${dayjs(
        w.globals.seriesX[seriesIndex][dataPointIndex]
      ).format("MMM YYYY")}</div>`;

      seriesData.forEach((item: any) => {
        const formattedValue = new Intl.NumberFormat("nl-NL", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(Number(item.value ?? 0) / 100);

        tooltipHtml += `
          <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
            <span class="apexcharts-tooltip-marker" style="background-color: ${
              item.color
            };"></span>
            <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
              <div class="apexcharts-tooltip-y-group">
                <span class="apexcharts-tooltip-text-y-label">${
                  item.name
                }: </span>
                <span class="apexcharts-tooltip-text-y-value">${formattedValue}</span>
              </div>
            </div>
          </div>
        `;
      });

      return tooltipHtml;
    },
  },
  yaxis: {
    title: {
      text: "Revenue (€)",
      style: {
        fontSize: "14px",
        fontWeight: "bold",
      },
    },
    labels: {
      formatter: function (value: number) {
        return new Intl.NumberFormat("nl-NL", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(Number(value ?? 0) / 100);
      },
    },
  },
  colors: [
    "#23C16B", // Green
    "#FFB323", // Yellow
    "#48A7F8", // Blue
    "#FF3333", // Red
    "#FF8800", // Orange
    "#9259FF", // Purple
    "#FF4DA6", // Pink
    "#33FFE6", // Teal
    "#808080", // Gray
    "#D17A50", // Brown
    "#FF7F50", // Coral
    "#8A2BE2", // BlueViolet
    "#5F9EA0", // CadetBlue
    "#DC143C", // Crimson
    "#00FFFF", // Cyan
    "#FF1493", // DeepPink
    "#696969", // DimGray
    "#B8860B", // DarkGoldenrod
    "#FF4500", // OrangeRed
    "#32CD32", // LimeGreen
    "#4682B4", // SteelBlue
    "#00FA9A", // MediumSpringGreen
    "#FFD700", // Gold
    "#FF6347", // Tomato
    "#7B68EE", // MediumSlateBlue
    "#008080", // Teal
    "#6A5ACD", // SlateBlue
    "#D2691E", // Chocolate
    "#B22222", // FireBrick
    "#9ACD32", // YellowGreen
    "#8B008B", // DarkMagenta
    "#00CED1", // DarkTurquoise
    "#FF69B4", // HotPink
    "#1E90FF", // DodgerBlue
    "#40E0D0", // Turquoise
    "#EE82EE", // Violet
  ],
};
